import React, { useState } from 'react';
import {
  View, Text, Image, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator,
} from 'react-native';
import { colors, spacing, radius, shadow } from '../theme';
import { openProductForPurchase } from '../services/PurchaseTrackingService';
import { displayProductReward } from '../services/CashbackDisplayService';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import TrendingBadge from './TrendingBadge';
import CalendarIcon from './CalendarIcon';
import FlatLayButton from './FlatLayButton';
import CompleteOutfitModal from './CompleteOutfitModal';
import { isOutfitIncomplete } from '../services/OutfitCompletionService';
import { displayName } from '../utils/displayName';

const CAT_EMOJI = {
  Robes:'👗', Hauts:'👚', Vestes:'🧥', Bas:'👖',
  Chaussures:'👟', Sacs:'👜', Bijoux:'💍', Accessoires:'✨',
};

// Schéma de score premium — v1.12.274 : labels via i18n.
// `t` est passé depuis le composant (hook useI18n défini dedans).
const scoreStyle = (sc, t) => {
  const tr = (k, fb) => (typeof t === 'function' ? t(k) : fb);
  if (sc >= 85) return { label: tr('score_exceptional', 'Exceptionnel'), color: '#1B5E20', bg: '#E8F5E9', grad: '#4CAF50', icon: '✨' };
  if (sc >= 70) return { label: tr('score_excellent',   'Excellent'),    color: '#0D47A1', bg: '#E3F2FD', grad: '#2196F3', icon: '👌' };
  if (sc >= 55) return { label: tr('score_good',        'Bien'),         color: '#E65100', bg: '#FFF3E0', grad: '#FF9800', icon: '👍' };
  if (sc >= 40) return { label: tr('score_decent',      'Correct'),      color: '#616161', bg: '#F5F5F5', grad: '#9E9E9E', icon: '💭' };
  return              { label: tr('score_to_improve',  'À améliorer'),  color: '#B71C1C', bg: '#FFEBEE', grad: '#F44336', icon: '🤔' };
};

const PARTNER_COLOR = {
  zalando:'#FF6900', asos:'#111111', hm:'#E50010', zara:'#000000', amazon:'#FF9900',
  cashmere:'#D4AF37', iqueens:'#5DADE2',
  rakuten:'#BF0000', tous:'#D4AF37', mango:'#E50010', // v1.12.158
};

const MISSING_TONE = {
  high:   { bg: '#FFEBEE', border: '#FFCDD2', text: '#C62828' },
  medium: { bg: '#FFF3E0', border: '#FFE0B2', text: '#E65100' },
  low:    { bg: '#F5F5F5', border: '#E0E0E0', text: '#616161' },
};

function SmartImage({ uri, style, fallback }) {
  const [dead, setDead] = useState(false);
  const [attempt, setAttempt] = useState(0); // 0 = URL originale, 1 = wsrv.nl, 2 = weserv.nl, 3 = dead

  // Normaliser l'URI : si c'est un objet ou array, prendre le premier string
  let normalizedUri = uri;
  if (Array.isArray(uri)) normalizedUri = uri[0];
  if (typeof normalizedUri !== 'string') normalizedUri = null;
  if (normalizedUri && !/^https?:\/\//.test(normalizedUri)) {
    // URL invalide (pas http/https) → fallback
    normalizedUri = null;
  }

  if (!normalizedUri || dead) return (
    <View style={[style, { backgroundColor:'#F5F5F5', alignItems:'center', justifyContent:'center' }]}>
      <Text style={{ fontSize: 28 }}>{fallback || '👗'}</Text>
    </View>
  );

  // Si l'URL est déjà un proxy wsrv.nl, extraire l'URL d'origine pour les fallbacks
  let originalUri = normalizedUri;
  if (normalizedUri.includes('wsrv.nl/?url=') || normalizedUri.includes('weserv.nl/?url=')) {
    try {
      const match = normalizedUri.match(/[?&]url=([^&]+)/);
      if (match) originalUri = decodeURIComponent(match[1]);
    } catch {}
  }

  const isZara = originalUri.includes('static.zara.net');
  // ═══ v1.9.30 : Auto-proxy TOUS (static.tous.com lent depuis certains réseaux) ═══
  const isTous = originalUri.includes('static.tous.com');
  const needsAutoProxy = isZara || isTous;
  const isS3 = originalUri.includes('s3.') || originalUri.includes('amazonaws.com');
  let src;
  if (attempt === 0) {
    // Première tentative : URL telle que fournie (éventuellement déjà proxy)
    // Pour Zara/TOUS : on force le proxy dès le départ
    // Pour S3 : on utilise l'URL directe (souvent une signed URL)
    src = needsAutoProxy ? 'https://wsrv.nl/?url=' + encodeURIComponent(originalUri) + '&w=400' : normalizedUri;
  } else if (attempt === 1) {
    // Tentative 2 : wsrv.nl sur l'URL d'origine (si pas déjà fait)
    // Pour S3 signed URLs : le proxy peut aider si l'URL elle-même expire
    src = 'https://wsrv.nl/?url=' + encodeURIComponent(originalUri) + '&w=400';
  } else if (attempt === 2) {
    // Tentative 3 : weserv.nl (mirror alternatif)
    src = 'https://images.weserv.nl/?url=' + encodeURIComponent(originalUri.replace(/^https?:\/\//, '')) + '&w=400';
  } else {
    src = originalUri; // dernier recours : URL brute
  }

  return (
    <Image source={{ uri: src }} style={style}
      onError={(e) => {
        console.warn('[OutfitCard SmartImage] failed attempt', attempt, ':', src?.slice(0, 80), e?.nativeEvent?.error);
        if (attempt < 3) setAttempt(attempt + 1);
        else setDead(true);
      }} />
  );
}

function SuggestCard({ p, onPress }) {
  const { t, lang } = useI18n();
  const partnerColor = PARTNER_COLOR[p.partner] || '#555';
  const emoji = p.emoji || CAT_EMOJI[p.category] || '👗';
  // v1.12.313 — Récompense en MONTANT (€) — jamais en %. Service centralisé.
  const { user } = useAuth();
  const reward = displayProductReward(p, user?.tier, lang);
  // v1.12.267 — Traduit les catégories FR ('Hauts'/'Bas'/'Vestes'/'Robes'...) selon langue
  const tCatLabel = (cat) => {
    const KEY = { 'Robes':'cat_dresses','Hauts':'cat_tops','Vestes':'cat_jackets','Manteaux':'cat_coats','Bas':'cat_bottoms','Chaussures':'cat_shoes','Sacs':'cat_bags','Bijoux':'cat_jewelry','Accessoires':'cat_accessories','Maillots':'cat_swimwear','Sport':'cat_sportswear' };
    return cat ? (KEY[cat] ? t(KEY[cat]) : cat) : '';
  };

  // ═══ TOUJOURS passer par onPress (parent ouvre le modal partagé) ═══
  // Avant : appelait openProductForPurchase direct → incohérent avec Catalogue
  // Maintenant : onPress(p) → parent fait setSelectedProduct(p) → ProductDetailModal
  const handlePress = () => {
    if (onPress) onPress();
  };

  return (
    <TouchableOpacity style={s.suggestCard} onPress={handlePress} activeOpacity={0.82}>
      <View style={s.suggestImg}>
        <SmartImage uri={p.image} style={s.suggestImgInner} fallback={emoji} />
        {p.isTrending && (
          <View style={s.trendingBadgeWrap}>
            <TrendingBadge tag={p.trendTag} compact />
          </View>
        )}
        {p._stylistScore != null && (
          <View style={s.stylistBadge}>
            <Text style={s.stylistBadgeText}>✨ {p._stylistScore}</Text>
          </View>
        )}
        {/* v1.12.313 — badge récompense en MONTANT € (jamais en %) */}
        {reward.amount > 0 && (
          <View style={s.cashbackBadge}>
            <Text style={s.cashbackText}>{reward.badge}</Text>
          </View>
        )}
      </View>
      <View style={s.suggestInfo}>
        <Text style={s.suggestCat}>{CAT_EMOJI[p.category]} {tCatLabel(p.category)}</Text>
        <Text style={s.suggestName} numberOfLines={2}>{displayName(p, lang)}</Text>
        <Text style={s.suggestBrand}>{p.brand}</Text>
        <View style={s.priceRow}>
          <Text style={s.suggestPrice}>{p.price} €</Text>
          {p.originalPrice > p.price + 0.5 && (
            <Text style={s.suggestOrigPrice}>{p.originalPrice} €</Text>
          )}
        </View>
        {/* Badge "Remplace votre X" si suggestion de remplacement */}
        {p._isReplacement && p._replacesItem ? (
          <View style={s.replacesBadge}>
            <Text style={s.replacesBadgeIcon}>🔄</Text>
            <Text style={s.replacesBadgeText} numberOfLines={1}>
              Remplace : {displayName(p._replacesItem, lang)}
            </Text>
          </View>
        ) : null}
      </View>
      <TouchableOpacity
        style={[s.partnerBtn, { backgroundColor: partnerColor }]}
        onPress={handlePress}
        activeOpacity={0.8}>
        <Text style={s.partnerBtnTxt}>Voir ↗</Text>
      </TouchableOpacity>
    </TouchableOpacity>
  );
}

// v1.12.267 — Map de traduction des catégories FR → clés i18n (cat_*)
const CAT_TO_KEY = {
  'Robes': 'cat_dresses', 'Hauts': 'cat_tops', 'Vestes': 'cat_jackets',
  'Manteaux': 'cat_coats', 'Bas': 'cat_bottoms', 'Chaussures': 'cat_shoes',
  'Sacs': 'cat_bags', 'Bijoux': 'cat_jewelry', 'Accessoires': 'cat_accessories',
  'Maillots': 'cat_swimwear', 'Sport': 'cat_sportswear',
};

function OutfitCardInner({ outfit, onSuggestPress, onItemPress, onLoadMore, loadingMore, onTryOn, onAddToCalendar, style }) {
  const { t } = useI18n();
  // Traduit une catégorie FR en label UPPER selon langue active
  const tCatUpper = (cat) => {
    if (!cat) return t('piece_caps');
    const k = CAT_TO_KEY[cat];
    return (k ? t(k) : cat).toUpperCase();
  };
  if (!outfit) return null;

  // v1.12.442 — recalibrage non-linéaire pour étaler la distribution écrasée.
  // v1.12.470 — exposant 2.2 → 1.3 : top combos à 88-98% (avant 80-97 jugé bas).
  // Mapping pow(x, 1.3) :
  //   raw 99 → 98 | raw 95 → 93 | raw 90 → 88 | raw 80 → 76 | raw 70 → 65
  //   raw 60 → 53 | raw 50 → 42 | raw 40 → 31
  // N'affecte QUE l'affichage, pas l'algo de tri/scoring.
  const rawScore = typeof outfit.score === 'number' ? outfit.score : 60;
  const score = Math.round(100 * Math.pow(Math.max(0, Math.min(100, rawScore)) / 100, 1.3));
  const scoreInfo = scoreStyle(score, t);
  const suggestions = Array.isArray(outfit.suggestions) ? outfit.suggestions : [];
  const missing = Array.isArray(outfit.missing)
    ? outfit.missing
    : (Array.isArray(outfit.missingCategories) ? outfit.missingCategories : []);

  // v1.12.123 — Bijoux + Accessoires masques par defaut, "Voir plus" pour expand
  const [showAccessories, setShowAccessories] = useState(false);

  const itemsAnalyzed = (outfit.items || []).filter(i => i.ai_metadata?.analyzedAt).length;
  const totalItems = (outfit.items || []).length;

  // ═══ Bouton "Compléter la tenue" supprimé en v1.7.43 ═══
  // Faisait doublon avec les suggestions partenaires juste au-dessus.
  // L'import CompleteOutfitModal et les styles `completeBtn*` sont
  // conservés au cas où on voudrait réintroduire la fonctionnalité ailleurs.

  return (
    <View style={[s.card, shadow.md, style]}>

      {/* Header avec score premium */}
      <View style={[s.header, { backgroundColor: scoreInfo.bg }]}>
        <View style={s.headerLeft}>
          {/* Tags dominants (styleTags IA) avec % match + badge tendance si applicable */}
          {Array.isArray(outfit.dominantTags) && outfit.dominantTags.length > 0 ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {outfit.dominantTags.slice(0, 3).map(tag => {
                const pct = outfit.tagMatches?.[tag] ? Math.round(outfit.tagMatches[tag] * 100) : null;
                // v1.12.560 — Traduction du tag via les clés i18n style_*
                const STYLE_LABEL_KEY = {
                  'élégant':'style_elegant','elegant':'style_elegant','casual':'style_casual',
                  'minimaliste':'style_minimal','minimal':'style_minimal',
                  'bohème':'style_boho','boheme':'style_boho','boho':'style_boho',
                  'soirée':'style_evening','soiree':'style_evening','evening':'style_evening',
                  'sport':'style_sport','sportif':'style_sport','sportive':'style_sport',
                  'bureau':'style_office','office':'style_office','workwear':'style_office','travail':'style_office',
                  'chic':'style_chic',
                  'urbain':'style_urban','urban':'style_urban','street':'style_urban','streetwear':'style_urban',
                  'romantique':'style_romantic','romantic':'style_romantic',
                  'vintage':'style_vintage','edgy':'style_edgy','preppy':'style_preppy',
                  'féminin':'style_feminine','feminin':'style_feminine','feminine':'style_feminine',
                  'structuré':'style_structured','structured':'style_structured',
                  'cérémonie':'style_ceremony','ceremonie':'style_ceremony','ceremony':'style_ceremony',
                };
                const key = STYLE_LABEL_KEY[(tag || '').toLowerCase()];
                const label = key ? t(key) : tag;
                return (
                  <View key={tag} style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 10, backgroundColor: '#111' }}>
                    <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff', letterSpacing: 0.3 }}>
                      {String(label).toUpperCase()}{pct !== null ? ` ${pct}%` : ''}
                    </Text>
                  </View>
                );
              })}
              {/* Badge Tendance si outfit contient ≥2 pièces tendance OU trendScore ≥70 */}
              {((outfit.trendyItemsCount || 0) >= 2 || (outfit.trendScore || 0) >= 70) && (
                <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 10, backgroundColor: '#FF4757' }}>
                  <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff', letterSpacing: 0.3 }}>
                    {t('badge_trending')}{outfit.trendScore ? ` ${outfit.trendScore}%` : ''}
                  </Text>
                </View>
              )}
            </View>
          ) : (outfit.occasion ? <Text style={s.occasion}>{outfit.occasion}</Text> : null)}
          <Text style={s.description}>
            {/* v1.12.436 — Tags compact style·occasion (au lieu de description bank longue).
                Pour bank: `style · occasion` du combo. Pour engine: bestTag · occasion. */}
            {(() => {
              const bank = outfit._bankCombination || {};
              const styleTag = bank.style || outfit.bestTag || outfit.style || '';
              const occTag = bank.occasion || outfit.occasion || '';
              const parts = [styleTag, occTag].filter(Boolean).map(s => String(s).trim());
              if (parts.length > 0) {
                return parts.join(' · ').replace(/^\w/, c => c.toUpperCase());
              }
              return outfit.description || outfit.label
                || t('outfit_n_pieces').replace('{count}', String(totalItems));
            })()}
          </Text>
          {/* ═══ Astuce de style si dispo (banque hebdo) ═══ */}
          {outfit._bankCombination?.style_tips && (
            <Text style={{ fontSize: 11, color: '#8B6F2E', marginTop: 4, fontStyle: 'italic' }}>
              💡 {outfit._bankCombination.style_tips}
            </Text>
          )}
          {/* v1.13.6 — tag "IA · x/x analysé" retiré des outfits (sur demande) */}
        </View>
        <View style={s.scoreBox}>
          <View style={[s.scoreCircle, { borderColor: scoreInfo.grad }]}>
            <Text style={[s.scoreNum, { color: scoreInfo.color }]}>{score}</Text>
            <Text style={[s.scoreSlash, { color: scoreInfo.color }]}>/100</Text>
          </View>
          <Text style={[s.scoreLabel, { color: scoreInfo.color }]}>
            {scoreInfo.icon} {scoreInfo.label}
          </Text>
        </View>
      </View>

      {/* Barre de progression */}
      <View style={s.scoreBarContainer}>
        <View style={s.scoreBarBg}>
          <View style={[s.scoreBarFill, { width: `${score}%`, backgroundColor: scoreInfo.grad }]} />
        </View>
      </View>

      {/* v1.12.92 — DEBUG SCORES box retirée sur demande user */}

      {/* Meta */}
      {(outfit.season || outfit.tempC != null || outfit.colors) && (
        <View style={s.meta}>
          {outfit.tempC != null && (
            <Text style={s.metaItem}>🌡️ {outfit.tempC}°C</Text>
          )}
          {outfit.season && (
            <Text style={s.metaItem}>
              {{ été:'☀️', 'mi-saison':'🍂', hiver:'❄️', printemps:'🌸' }[outfit.season] || '🌡️'} {outfit.season}
            </Text>
          )}
          {outfit.colors && outfit.colors.length > 0 && (
            <Text style={s.metaItem}>🎨 {outfit.colors.slice(0, 3).join(', ')}</Text>
          )}
        </View>
      )}

      {/* ═══ Articles : piece principale a GAUCHE + autres a DROITE (v1.12.131) ═══ */}
      {/* Hero (1er selon categoryOrder) prend ~58% a gauche en grand, le reste     */}
      {/* est empilee en colonne a droite. Toutes les images en `contain`.          */}
      {(() => {
        const items = outfit.items || [];
        if (items.length === 0) return null;

        const isAccessoryOrJewel = (cat) => /accessoire|access|bijou|jewel/i.test(cat || '');
        const categoryOrder = (cat) => {
          const c = (cat || '').toLowerCase();
          if (/robe|dress/.test(c)) return 1;
          if (/haut|top|chemise|t-?shirt|pull|veste|sweat|polo|blouson|manteau|cardigan/.test(c)) return 2;
          if (/bas|pantalon|jupe|short|jeans/.test(c)) return 3;
          if (/chaussure|shoe|basket|sneaker|boot/.test(c)) return 4;
          if (/sac|bag/.test(c)) return 5;
          if (/accessoire|access/.test(c)) return 6;
          if (/bijou|jewel/.test(c)) return 7;
          return 8;
        };
        const sorted = [...items].sort((a, b) => categoryOrder(a.category) - categoryOrder(b.category));

        const mainItems = sorted.filter(it => !isAccessoryOrJewel(it.category));
        const visibleItems = showAccessories ? sorted : mainItems;
        const n = visibleItems.length;
        if (n === 0) return null;

        const hero = visibleItems[0];
        const rest = visibleItems.slice(1);
        const restN = rest.length;
        const heroAI = !!hero.ai_metadata?.analyzedAt;

        // Hero seul : full width
        if (restN === 0) {
          return (
            <View style={s.gridWrap}>
              <TouchableOpacity
                style={s.heroSolo}
                onPress={() => onItemPress && onItemPress(hero)}
                activeOpacity={0.85}
              >
                <SmartImage
                  uri={hero.image || hero.image_url}
                  style={s.heroTileImg}
                  fallback={hero.emoji || CAT_EMOJI[hero.category] || '👗'}
                />
                {hero.discount > 10 ? (
                  <View style={s.gridItemDiscount}>
                    <Text style={s.gridItemDiscountTxt}>-{hero.discount}%</Text>
                  </View>
                ) : null}
                {heroAI && (
                  <View style={s.gridItemAi}>
                    <Text style={s.gridItemAiTxt}>✨</Text>
                  </View>
                )}
                <View style={s.heroTileLabel}>
                  <Text style={s.heroTileLabelCat} numberOfLines={1}>
                    ⭐ {tCatUpper(hero.category)} — {t('main_piece_caps')}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          );
        }

        // Hero gauche + autres en colonne droite. Si > 4 articles a droite,
        // on bascule en grille 2 cols pour eviter que les tuiles soient minuscules.
        const rightGrid = restN >= 5;

        return (
          <View style={s.gridWrap}>
            <View style={s.heroRow}>
              {/* GAUCHE — piece principale */}
              <TouchableOpacity
                style={s.heroLeft}
                onPress={() => onItemPress && onItemPress(hero)}
                activeOpacity={0.85}
              >
                <SmartImage
                  uri={hero.image || hero.image_url}
                  style={s.heroTileImg}
                  fallback={hero.emoji || CAT_EMOJI[hero.category] || '👗'}
                />
                {hero.discount > 10 ? (
                  <View style={s.gridItemDiscount}>
                    <Text style={s.gridItemDiscountTxt}>-{hero.discount}%</Text>
                  </View>
                ) : null}
                {heroAI && (
                  <View style={s.gridItemAi}>
                    <Text style={s.gridItemAiTxt}>✨</Text>
                  </View>
                )}
                <View style={s.heroTileLabel}>
                  <Text style={s.heroTileLabelCat} numberOfLines={2}>
                    ⭐ {tCatUpper(hero.category)}
                  </Text>
                </View>
              </TouchableOpacity>

              {/* DROITE — autres pieces */}
              <View style={rightGrid ? s.rightGrid : s.rightCol}>
                {rest.map((p, j) => {
                  const hasAI = !!p.ai_metadata?.analyzedAt;
                  return (
                    <TouchableOpacity
                      key={`item_${j}_${p.id || p.name || 'x'}`}
                      style={rightGrid ? s.rightGridItem : s.rightItem}
                      onPress={() => onItemPress && onItemPress(p)}
                      activeOpacity={0.85}
                    >
                      <SmartImage
                        uri={p.image || p.image_url}
                        style={s.gridTileImg}
                        fallback={p.emoji || CAT_EMOJI[p.category] || '👗'}
                      />
                      {p.discount > 10 ? (
                        <View style={s.gridItemDiscount}>
                          <Text style={s.gridItemDiscountTxt}>-{p.discount}%</Text>
                        </View>
                      ) : null}
                      {hasAI && (
                        <View style={s.gridItemAi}>
                          <Text style={s.gridItemAiTxt}>✨</Text>
                        </View>
                      )}
                      <View style={s.gridTileLabel}>
                        <Text style={s.gridTileLabelCat} numberOfLines={1}>
                          {p.category === 'Bijoux' ? `💍 ${t('cat_jewelry').toUpperCase()}`
                            : p.category === 'Accessoires' ? `🧣 ${t('cat_accessories_short').toUpperCase()}`
                            : tCatUpper(p.category)}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>
        );
      })()}

      {/* v1.12.123 — Bouton "Voir plus / Masquer" (accessoires + bijoux) */}
      {(() => {
        const items = outfit.items || [];
        const accCount = items.filter(it => /accessoire|access|bijou|jewel/i.test(it.category || '')).length;
        if (accCount === 0) return null;
        return (
          <TouchableOpacity
            onPress={() => setShowAccessories(v => !v)}
            activeOpacity={0.75}
            style={s.toggleAccBtn}>
            <Text style={s.toggleAccTxt}>
              {showAccessories
                ? `↑ ${t('hide_accessories_jewels')}`
                : `↓ ${t('view_n_accessories').replace('{count}', String(accCount))}`}
            </Text>
          </TouchableOpacity>
        );
      })()}

      {/* v1.12.95 — Bloc "Pour compléter ce look" retiré sur demande user */}

      {/* ═══ v1.10.15 : Suggestions partenaires SUPPRIMÉES ═══ */}
      {/* Avant : bloc "🛍️ Notre sélection pour ce look" affichait des articles */}
      {/* partenaires (Zalando/Zara/...) sous chaque tenue dans LooksScreen.    */}
      {/* Maintenant : on garde uniquement les pièces du dressing user.         */}
      {/* Si l'user veut des compléments, il peut aller dans Catalogue.         */}

      {/* ═══ v1.10.16 + v1.12.191 + v1.12.834 : Boutons Try-on + Flat-Lay + Calendrier ═══ */}
      {(onTryOn || onAddToCalendar) && (
        <View style={{
          marginTop: 12, marginHorizontal: 16, marginBottom: 16,
          flexDirection: 'row', gap: 8,
        }}>
          {onTryOn && (
            <TouchableOpacity
              onPress={() => onTryOn(outfit)}
              activeOpacity={0.85}
              style={{
                flex: 1,
                backgroundColor: '#1A1A1A',
                paddingVertical: 12,
                borderRadius: 8,
                alignItems: 'center', justifyContent: 'center',
                borderWidth: 1, borderColor: '#D4AF72',
              }}>
              <Text style={{ fontSize: 12, fontWeight: '700', color: '#D4AF72', letterSpacing: 1 }}>
                ✨ {t('try_on_caps')}
              </Text>
            </TouchableOpacity>
          )}
          {/* v1.12.834 — Flat-Lay button (meme que Tenue du jour + Selection styliste) */}
          {onTryOn && (
            <FlatLayButton outfit={outfit} style={{ borderRadius: 8 }} />
          )}
          {onAddToCalendar && (
            <TouchableOpacity
              onPress={() => onAddToCalendar(outfit)}
              activeOpacity={0.85}
              style={{
                paddingVertical: 10, paddingHorizontal: 14,
                borderRadius: 8, alignItems: 'center', justifyContent: 'center',
                borderWidth: 1, borderColor: '#D4AF72',
                backgroundColor: 'transparent',
              }}>
              {/* v1.12.538 — Même icône que le bouton calendrier en haut de l'accueil */}
              <CalendarIcon size={22} color="#1A1A1A" dot="#D4AF72" />
            </TouchableOpacity>
          )}
        </View>
      )}

    </View>
  );
}

const s = StyleSheet.create({
  // v1.12.117 — maxWidth: 460px sur web pour éviter les cards géantes
  // sur desktop (le contenu est déjà clampé à 720px par App.js, mais une
  // OutfitCard centrée plus étroite reste plus lisible que 100% du frame).
  card: { backgroundColor:'#FFFFFF', borderRadius:radius.lg, borderWidth:1, borderColor:colors.bg.border, marginBottom:spacing.lg, overflow: 'hidden', maxWidth: 460, alignSelf: 'center', width: '100%' },

  header: { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', padding:spacing.lg, paddingBottom:spacing.md },
  headerLeft: { flex:1, paddingRight: 10 },
  occasion: { fontSize:9, letterSpacing:2.5, color:colors.text.faint, textTransform:'uppercase', marginBottom:4, fontWeight: '600' },
  description: { fontSize:15, fontWeight:'600', color:colors.text.primary, lineHeight:20 },
  aiTag: { flexDirection: 'row', alignItems: 'center', marginTop: 6, alignSelf: 'flex-start', backgroundColor: 'rgba(255,255,255,0.7)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  aiTagIcon: { fontSize: 10, marginRight: 4 },
  aiTagText: { fontSize: 10, color: colors.text.secondary, fontWeight: '500' },

  scoreBox: { alignItems:'center' },
  scoreCircle: { width: 68, height: 68, borderRadius: 34, borderWidth: 3, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF' },
  scoreNum: { fontSize:22, fontWeight:'700', lineHeight: 26 },
  scoreSlash: { fontSize: 9, opacity: 0.7, marginTop: -2 },
  scoreLabel: { fontSize:10, letterSpacing:0.5, marginTop:4, fontWeight: '600', textAlign: 'center' },

  scoreBarContainer: { paddingHorizontal: spacing.lg, paddingTop: 4, paddingBottom: spacing.sm },
  scoreBarBg: { height: 4, backgroundColor: '#F0F0F0', borderRadius: 2, overflow: 'hidden' },
  scoreBarFill: { height: '100%', borderRadius: 2 },

  // ═══ DEBUG box (Phase A v1.12.56) ═══
  debugBox: {
    marginHorizontal: spacing.lg,
    marginTop: 4,
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#FFF8E1',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FFE082',
  },
  debugTitle: {
    fontSize: 9,
    fontWeight: '700',
    color: '#8B6F2E',
    letterSpacing: 1.2,
    marginBottom: 4,
  },
  debugLine: {
    fontSize: 10,
    color: '#5D4037',
    lineHeight: 14,
    marginBottom: 1,
  },
  debugVal: {
    fontWeight: '700',
    color: '#3E2723',
  },

  // ═══ Bouton "Compléter la tenue" ═══
  completeBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    margin: spacing.lg, marginTop: 0,
    padding: 14,
    backgroundColor: '#FFF7E0',
    borderRadius: 12,
    borderWidth: 1, borderColor: '#D4AF72',
  },
  completeBtnIcon: { fontSize: 22 },
  completeBtnLabel: {
    fontSize: 14, fontWeight: '700', color: '#1A1814', letterSpacing: 0.3,
  },
  completeBtnSub: {
    fontSize: 11, color: '#7A6F5C', marginTop: 2,
  },
  completeBtnArrow: {
    fontSize: 22, color: '#B8860B', fontWeight: '600',
  },

  meta: { flexDirection:'row', alignItems:'center', paddingHorizontal:spacing.lg, paddingBottom:spacing.md, gap:10, flexWrap:'wrap' },
  metaItem: { fontSize:11, color:colors.text.secondary, fontWeight: '500' },

  itemsRow: { paddingHorizontal:spacing.lg, paddingBottom:spacing.md, gap:12 },
  itemBox: { alignItems:'center', width:76 },
  itemImgWrap: { width: 72, height: 88, position: 'relative' },
  // ═══ Layout : piece principale a GAUCHE + autres a DROITE (v1.12.131) ═══
  // Hero gauche (~58%, aspect 4/5) + colonne droite (flex 1) avec items empiles.
  // Si > 4 items a droite : bascule en grille 2 cols. Images toujours en `contain`.
  gridWrap: { padding: spacing.md },
  heroRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'stretch',
  },
  heroLeft: {
    width: '52%',
    aspectRatio: 4 / 5,
    backgroundColor: '#F5F2ED',
    borderRadius: radius.md,
    overflow: 'hidden',
    position: 'relative',
  },
  heroSolo: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: '#F5F2ED',
    borderRadius: radius.md,
    overflow: 'hidden',
    position: 'relative',
  },
  heroTileImg: {
    width: '100%', height: '100%',
    resizeMode: 'contain',
  },
  heroTileLabel: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.65)',
    paddingVertical: 8, paddingHorizontal: 10,
  },
  heroTileLabelCat: {
    fontSize: 10, fontWeight: '700', color: '#FFD700',
    letterSpacing: 0.6, textAlign: 'center',
  },
  rightCol: {
    flex: 1,
    flexDirection: 'column',
    gap: 6,
  },
  rightItem: {
    flex: 1,
    minHeight: 56,
    backgroundColor: '#F5F2ED',
    borderRadius: radius.md,
    overflow: 'hidden',
    position: 'relative',
  },
  rightGrid: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'space-between',
    gap: 6,
  },
  rightGridItem: {
    width: '48%',
    aspectRatio: 1,
    backgroundColor: '#F5F2ED',
    borderRadius: radius.md,
    overflow: 'hidden',
    position: 'relative',
  },
  gridFlex: {
    flexDirection: 'row', flexWrap: 'wrap',
    gap: 8,
  },
  gridCell: {
    aspectRatio: 1,
  },
  gridTile: {
    flex: 1,
    backgroundColor: '#F5F2ED',
    borderRadius: radius.md,
    overflow: 'hidden',
    position: 'relative',
    margin: 4,
  },
  gridTileImg: {
    width: '100%', height: '100%',
    resizeMode: 'contain', // PRESERVE l'image complete sans crop
  },
  gridTileLabel: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingVertical: 4, paddingHorizontal: 6,
  },
  gridTileLabelCat: {
    fontSize: 9, fontWeight: '700', color: '#fff',
    letterSpacing: 0.5, textAlign: 'center',
  },
  // ═══ Anciens styles hero/stack (deprecies, gardes pour compat) ═══
  heroLayout: { flexDirection: 'row', padding: spacing.md, gap: spacing.sm },
  heroBox: {
    width: '58%', aspectRatio: 0.75,
    backgroundColor: '#F5F2ED', borderRadius: radius.md,
    overflow: 'hidden', position: 'relative',
  },
  heroImg: { width: '100%', height: '100%', resizeMode: 'cover' },
  heroDiscountBadge: {
    position: 'absolute', top: 8, left: 8,
    backgroundColor: '#E53935',
    paddingHorizontal: 7, paddingVertical: 3, borderRadius: 4,
  },
  heroDiscountTxt: { fontSize: 10, fontWeight: '700', color: '#fff' },
  heroAiBadge: {
    position: 'absolute', top: 8, right: 8,
    backgroundColor: 'rgba(255,215,0,0.95)',
    width: 22, height: 22, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  heroAiTxt: { fontSize: 11 },
  heroOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingVertical: 6, paddingHorizontal: 10,
  },
  heroOverlayCat: { fontSize: 9, fontWeight: '700', color: '#fff', letterSpacing: 1.2 },
  heroOverlayName: { fontSize: 10, color: '#fff', marginTop: 1 },
  // Stack vertical droite : flex column, items s'etendent en hauteur egale
  rightStack: { flex: 1, flexDirection: 'column', gap: 6 },
  rightItem: {
    flex: 1,
    backgroundColor: '#F5F2ED', borderRadius: radius.sm,
    overflow: 'hidden', position: 'relative',
  },
  // Overlay "+N" sur le dernier item quand il y a plus d'articles non visibles
  overflowOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center', justifyContent: 'center',
  },
  overflowText: { fontSize: 22, fontWeight: '700', color: '#fff', letterSpacing: 1 },
  // v1.12.123 — bouton voir plus accessoires/bijoux
  toggleAccBtn: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    paddingVertical: 10,
    borderRadius: radius.md,
    backgroundColor: '#FAFAFA',
    borderWidth: 1, borderColor: colors.bg.border,
    alignItems: 'center',
  },
  toggleAccTxt: {
    fontSize: 11, fontWeight: '600',
    color: colors.text.secondary, letterSpacing: 1,
  },
  emptyRightHint: {
    flex: 1,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#FAFAFA',
    borderRadius: radius.sm,
    padding: 8,
  },
  emptyRightHintTxt: {
    fontSize: 10, color: colors.text.faint, textAlign: 'center',
  },
  // Conserve gridItem pour compat (ancien code peut s'y referencer)
  gridItem: {
    backgroundColor: '#F5F2ED', borderRadius: radius.sm,
    overflow: 'hidden', position: 'relative',
  },
  gridItemImg: { width: '100%', height: '100%', resizeMode: 'cover' },
  gridItemDiscount: {
    position: 'absolute', top: 4, left: 4,
    backgroundColor: '#E53935',
    paddingHorizontal: 4, paddingVertical: 1.5, borderRadius: 3,
  },
  gridItemDiscountTxt: { fontSize: 8, fontWeight: '700', color: '#fff' },
  gridItemAi: {
    position: 'absolute', top: 4, right: 4,
    backgroundColor: 'rgba(255,215,0,0.95)',
    width: 18, height: 18, borderRadius: 9,
    alignItems: 'center', justifyContent: 'center',
  },
  gridItemAiTxt: { fontSize: 9 },
  gridItemCat: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingVertical: 3, paddingHorizontal: 4,
  },
  gridItemCatTxt: { fontSize: 7, fontWeight: '700', color: '#fff', letterSpacing: 0.5, textAlign: 'center' },
  itemImg: { width:72, height:88, borderRadius:radius.md, resizeMode:'cover' },
  itemBadge: { position:'absolute', top:4, right:4, backgroundColor:'rgba(255,255,255,0.92)', borderRadius:10, width: 20, height: 20, alignItems: 'center', justifyContent: 'center' },
  itemBadgeText: { fontSize:11 },
  itemAiDot: { position: 'absolute', bottom: 4, right: 4, backgroundColor: '#FFD700', borderRadius: 8, width: 16, height: 16, alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: '#FFF' },
  itemAiDotText: { fontSize: 8 },
  itemName: { fontSize:10, color:colors.text.secondary, marginTop:6, textAlign:'center', fontWeight: '500' },
  itemColor: { fontSize:9, color:colors.text.faint, textAlign:'center', marginTop: 1, fontStyle: 'italic' },

  missingBlock: { marginHorizontal:spacing.lg, marginBottom:spacing.md, padding:spacing.md, backgroundColor:'#FAFAFA', borderRadius:radius.md, borderWidth:1, borderColor:'#EEE' },
  missingHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 6 },
  missingIcon: { fontSize: 16 },
  missingTitle: { fontSize:12, letterSpacing:0.5, color:colors.text.primary, fontWeight: '700' },
  missingList: { gap: 6 },
  missingPill: { flexDirection: 'row', alignItems: 'center', paddingHorizontal:12, paddingVertical:8, borderRadius:radius.md, borderWidth:1, gap: 10 },
  missingPillIcon: { fontSize: 18 },
  missingPillCat: { fontSize: 12, fontWeight: '700' },
  missingPillReason: { fontSize: 10, marginTop: 1 },

  suggestBlock: { borderTopWidth:1, borderTopColor:colors.bg.border, backgroundColor: '#FCFCFC' },
  suggestTitleRow: { padding:spacing.md, paddingBottom:spacing.sm },
  suggestSectionTitle: { fontSize:13, fontWeight:'700', color:colors.text.primary },
  suggestSectionSub: { fontSize: 10, color: colors.text.faint, marginTop: 2 },
  suggestRow: { paddingHorizontal:spacing.md, paddingBottom:spacing.md, gap:12, alignItems:'flex-start' },

  loadMoreCard: {
    width: 130, height: 210,
    backgroundColor: '#FFF9E6',
    borderWidth: 1.5, borderColor: '#F5DEB3',
    borderStyle: 'dashed',
    borderRadius: 14,
    alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: 12,
  },
  loadMoreIconWrap: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: '#F5DEB3',
    alignItems: 'center', justifyContent: 'center',
  },
  loadMoreIcon: { fontSize: 22 },
  loadMoreText: { fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5, textAlign: 'center' },
  loadMoreSubText: { fontSize: 9, color: '#999', letterSpacing: 0.3, textAlign: 'center' },
  suggestCard: { width:152, backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:'#EEE', overflow: 'hidden' },
  suggestImg: { width:'100%', height:170, position:'relative', backgroundColor:'#F5F5F5', alignItems:'center', justifyContent:'center' },
  suggestImgInner: { width:'100%', height:'100%', resizeMode:'cover' },
  stylistBadge: { position:'absolute', top:6, left:6, borderRadius:10, paddingHorizontal:7, paddingVertical:3, backgroundColor: 'rgba(0,0,0,0.75)' },
  stylistBadgeText: { fontSize:10, color:'#FFD700', fontWeight:'700' },
  trendingBadgeWrap: { position:'absolute', top:6, right:6, zIndex:2 },
  cashbackBadge: { position: 'absolute', top: 6, right: 6, paddingHorizontal: 6, height: 20, borderRadius: 10, backgroundColor: '#4CAF50', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' },
  cashbackText: { fontSize: 9, color: '#FFF', fontWeight: '700', letterSpacing: 0.3 },
  suggestInfo: { padding:10 },
  suggestCat: { fontSize:9, color:'#999', letterSpacing:1, textTransform:'uppercase', marginBottom:3 },
  suggestName: { fontSize:11, fontWeight:'600', color:'#111', lineHeight:14, marginBottom:3, minHeight: 28 },
  suggestBrand: { fontSize:10, color:'#999', marginBottom:4 },
  priceRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  suggestPrice: { fontSize:14, fontWeight:'700', color:'#111' },
  suggestOrigPrice: { fontSize: 10, color: '#999', textDecorationLine: 'line-through' },
  replacesBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 6,
    paddingHorizontal: 6,
    paddingVertical: 4,
    backgroundColor: '#FFF7ED',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FED7AA',
  },
  replacesBadgeIcon: { fontSize: 10 },
  replacesBadgeText: {
    fontSize: 9,
    fontWeight: '600',
    color: '#9A3412',
    flex: 1,
  },
  partnerBtn: { margin:8, marginTop:4, paddingVertical:9, borderRadius:6, alignItems:'center' },
  partnerBtnTxt: { fontSize:10, fontWeight:'700', color:'#fff', letterSpacing:0.5 },
});

// ═══ Wrap avec React.memo : ne re-render que si outfit change réellement ═══
export default React.memo(OutfitCardInner, (prev, next) => {
  return (
    prev.outfit?.id === next.outfit?.id &&
    prev.loadingMore === next.loadingMore &&
    (prev.outfit?.suggestions?.length || 0) === (next.outfit?.suggestions?.length || 0) &&
    (prev.outfit?.dominantTags?.join('|') || '') === (next.outfit?.dominantTags?.join('|') || '')
  );
});
